import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  MessageSquare, Send, Loader2, Bot, User, Sparkles, 
  Clock, Trash2, Plus, HelpCircle, Stethoscope, Calendar,
  Shield, FileText, Users
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Quick suggestion topics
const QUICK_TOPICS = [
  { icon: Calendar, label: "Scheduling", questions: [
    "What is the optimal RNCM caseload?",
    "How should I handle visit scheduling conflicts?",
    "What are the on-call coverage requirements?"
  ]},
  { icon: Stethoscope, label: "Clinical", questions: [
    "What are the HVLDL requirements?",
    "How often should aide supervision visits occur?",
    "What is required in a comprehensive assessment?"
  ]},
  { icon: Shield, label: "Compliance", questions: [
    "What are the IDG meeting requirements?",
    "What documentation is required for physician orders?",
    "What are the CHAP survey expectations?"
  ]},
  { icon: FileText, label: "Regulations", questions: [
    "What are the Medicare CoPs for hospice?",
    "What are CMS quality reporting requirements?",
    "What are the bereavement service requirements?"
  ]}
];

export default function AIAgentPage() {
  const { token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    fetchSessions();
  }, [token]);

  const fetchSessions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ai-agent/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSessions(data);
      }
    } catch (error) {
      console.error('Failed to fetch sessions');
    }
  };

  const loadSession = async (sid) => {
    try {
      const response = await fetch(`${API_URL}/api/ai-agent/history/${sid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages.map(m => ({
          role: m.role,
          content: m.content,
          timestamp: m.created_at
        })));
        setSessionId(sid);
        setShowSuggestions(false);
      }
    } catch (error) {
      toast.error('Failed to load conversation');
    }
  };

  const sendMessage = async (messageText = inputMessage) => {
    if (!messageText.trim()) return;

    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);
    setShowSuggestions(false);

    try {
      const response = await fetch(`${API_URL}/api/ai-agent/chat?message=${encodeURIComponent(messageText)}${sessionId ? `&session_id=${sessionId}` : ''}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSessionId(data.session_id);
        
        const assistantMessage = {
          role: 'assistant',
          content: data.response,
          timestamp: data.timestamp
        };
        
        setMessages(prev => [...prev, assistantMessage]);
        fetchSessions();
      } else {
        toast.error('Failed to get response');
      }
    } catch (error) {
      toast.error('Error communicating with AI agent');
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const startNewConversation = () => {
    setMessages([]);
    setSessionId(null);
    setShowSuggestions(true);
    inputRef.current?.focus();
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await fetch(`${API_URL}/api/ai-agent/session/${sid}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(prev => prev.filter(s => s.session_id !== sid));
      if (sessionId === sid) {
        startNewConversation();
      }
      toast.success('Conversation deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="h-[calc(100vh-120px)] flex gap-4" data-testid="ai-agent-page">
      {/* Sidebar - Previous Conversations */}
      <Card className="w-72 flex-shrink-0 hidden lg:flex flex-col">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Conversations</CardTitle>
            <Button size="sm" variant="ghost" onClick={startNewConversation} data-testid="new-chat-btn">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-hidden p-2">
          <ScrollArea className="h-full">
            <div className="space-y-1">
              {sessions.map((session) => (
                <div
                  key={session.session_id}
                  onClick={() => loadSession(session.session_id)}
                  className={`p-2 rounded-lg cursor-pointer group flex items-center justify-between hover:bg-slate-100 dark:hover:bg-slate-800 ${sessionId === session.session_id ? 'bg-slate-100 dark:bg-slate-800' : ''}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{session.preview}</p>
                    <p className="text-xs text-muted-foreground">{session.messages} messages</p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0"
                    onClick={(e) => deleteSession(session.session_id, e)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
              {sessions.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No conversations yet</p>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Main Chat Area */}
      <Card className="flex-1 flex flex-col">
        <CardHeader className="pb-2 border-b">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#00b0ab] to-[#008a86] flex items-center justify-center">
              <Bot className="h-5 w-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg">Hospice Operations Assistant</CardTitle>
              <CardDescription>Ask about scheduling, clinical operations, compliance, and regulations</CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-hidden p-0">
          <ScrollArea className="h-full p-4">
            {/* Welcome / Suggestions */}
            {showSuggestions && messages.length === 0 && (
              <div className="space-y-6 py-4">
                <div className="text-center">
                  <Sparkles className="h-12 w-12 mx-auto mb-4 text-[#00b0ab]" />
                  <h3 className="text-lg font-semibold mb-2">How can I help you today?</h3>
                  <p className="text-sm text-muted-foreground max-w-md mx-auto">
                    I can answer questions about hospice scheduling, clinical operations, 
                    compliance requirements, and regulatory standards.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                  {QUICK_TOPICS.map((topic, idx) => (
                    <Card key={idx} className="hover:border-indigo-300 transition-colors">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <topic.icon className="h-4 w-4 text-[#00b0ab]" />
                          {topic.label}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-1">
                          {topic.questions.map((q, qIdx) => (
                            <Button
                              key={qIdx}
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start text-left h-auto py-1.5 px-2 text-xs font-normal"
                              onClick={() => sendMessage(q)}
                              data-testid={`suggestion-${idx}-${qIdx}`}
                            >
                              <HelpCircle className="h-3 w-3 mr-2 flex-shrink-0" />
                              <span className="truncate">{q}</span>
                            </Button>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Messages */}
            <div className="space-y-4">
              {messages.map((message, idx) => (
                <div
                  key={idx}
                  className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {message.role === 'assistant' && (
                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#00b0ab] to-[#008a86] flex items-center justify-center flex-shrink-0">
                      <Bot className="h-4 w-4 text-white" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      message.role === 'user'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-100 dark:bg-slate-800'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    <p className={`text-xs mt-1 ${message.role === 'user' ? 'text-indigo-200' : 'text-muted-foreground'}`}>
                      {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  {message.role === 'user' && (
                    <div className="h-8 w-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                      <User className="h-4 w-4" />
                    </div>
                  )}
                </div>
              ))}

              {loading && (
                <div className="flex gap-3 justify-start">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#00b0ab] to-[#008a86] flex items-center justify-center">
                    <Bot className="h-4 w-4 text-white" />
                  </div>
                  <div className="bg-slate-100 dark:bg-slate-800 rounded-2xl px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>
        </CardContent>

        {/* Input Area */}
        <div className="p-4 border-t">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about hospice operations, scheduling, compliance..."
              disabled={loading}
              className="flex-1"
              data-testid="chat-input"
            />
            <Button 
              onClick={() => sendMessage()} 
              disabled={loading || !inputMessage.trim()}
              className="bg-gradient-to-r from-[#00b0ab] to-[#008a86]"
              data-testid="send-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Answers are based on hospice regulations and best practices. Always verify with your compliance officer.
          </p>
        </div>
      </Card>
    </div>
  );
}
